// components/FilterSelector.tsx
import React, { useState, useEffect } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { Search } from "@mui/icons-material";
import { tags } from "../../reusables/tags";

interface FilterSelectorProps {
  searchTerm: string;
  setSearchTerm: (val: string) => void;

  selectedTags: string[];
  setSelectedTags: (val: string[]) => void;

  showClosed: boolean;
  setShowClosed: (val: boolean) => void;

  distance: number;
  setDistance: (val: number) => void;

  onLocationClick: () => void;
}

const FilterSelector: React.FC<FilterSelectorProps> = ({
  searchTerm,
  setSearchTerm,
  selectedTags,
  setSelectedTags,
  showClosed,
  setShowClosed,
  distance,
  setDistance,
  onLocationClick,
}) => {
  const [openDialog, setOpenDialog] = useState(false);

  // Temporary states for dialog changes
  const [tempSelectedTags, setTempSelectedTags] =
    useState<string[]>(selectedTags);
  const [tempShowClosed, setTempShowClosed] = useState<boolean>(showClosed);
  const [tempDistance, setTempDistance] = useState<number>(distance);

  useEffect(() => {
    // Sync if parent updates
    setTempSelectedTags(selectedTags);
    setTempShowClosed(showClosed);
    setTempDistance(distance);
  }, [selectedTags, showClosed, distance]);

  const handleDialogOpen = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  // Apply changes to parent
  const handleApply = () => {
    setSelectedTags(tempSelectedTags);
    setShowClosed(tempShowClosed);

    // Persist changed settings to localStorage if desired
    if (tempDistance !== distance) {
      sessionStorage.setItem("distance", String(tempDistance));
    }
    if (tempShowClosed !== showClosed) {
      localStorage.setItem("showClosed", String(tempShowClosed));
    }

    setDistance(tempDistance);
    handleDialogClose();
  };

  // Revert changes
  const handleCancel = () => {
    setTempSelectedTags(selectedTags);
    setTempShowClosed(showClosed);
    setTempDistance(distance);
    handleDialogClose();
  };

  return (
    <>
      {/* Search Field (white background for visibility on dark pages) */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <TextField
          variant="outlined"
          placeholder="Search by Store Name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          fullWidth
          sx={{
            backgroundColor: "#fff",
            borderRadius: 1,
            maxWidth: 400,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {/* My Location icon on the left */}
                <IconButton onClick={onLocationClick}>
                  <MyLocationIcon />
                </IconButton>
                {/* Filter icon on the right */}
                <IconButton onClick={handleDialogOpen}>
                  <TuneIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filter Dialog (dark-themed) */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: "#0D1B2A", // Very dark blue background
            color: "white",
            px: 2,
            py: 3,
          },
        }}
      >
        <DialogTitle sx={{ color: "white" }}>Filter Options</DialogTitle>
        <DialogContent>
          {/* Distance Dropdown */}
          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom>
              Distance (km)
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="distance-label" sx={{ color: "white" }}>
                Distance (km)
              </InputLabel>
              <Select
                labelId="distance-label"
                label="Distance (km)"
                value={tempDistance}
                onChange={(e) => setTempDistance(e.target.value as number)}
                sx={{
                  color: "white",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.6)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "#1B2638",
                      color: "white",
                    },
                  },
                }}
              >
                <MenuItem value={1}>1 km</MenuItem>
                <MenuItem value={2}>2 km</MenuItem>
                <MenuItem value={5}>5 km</MenuItem>
                <MenuItem value={10}>10 km</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Tags Autocomplete */}
          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom>
              Tags (up to 3)
            </Typography>
            <Autocomplete
              multiple
              limitTags={2}
              options={tags}
              value={tempSelectedTags}
              onChange={(event, newValue) => {
                // Limit to max of 3 tags
                if (newValue.length <= 3) {
                  setTempSelectedTags(newValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select up to 3 tags"
                  sx={{
                    ".MuiOutlinedInput-root": {
                      bgcolor: "#193043",
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.6)",
                      },
                      "&:hover fieldset": {
                        borderColor: "white",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "white",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "white",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "white",
                    },
                  }}
                  InputLabelProps={{ sx: { color: "white" } }}
                />
              )}
              sx={{
                "& .MuiAutocomplete-popupIndicator": {
                  color: "white",
                },
              }}
              popupIcon={<Search sx={{ color: "white" }} />}
            />
          </Box>

          {/* Show Closed Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={tempShowClosed}
                onChange={(e) => setTempShowClosed(e.target.checked)}
                sx={{
                  color: "white",
                  "&.Mui-checked": {
                    color: "#66BB6A",
                  },
                }}
              />
            }
            label="Show Closed"
            sx={{ color: "white" }}
          />
        </DialogContent>

        <DialogActions sx={{ pt: 2 }}>
          <Button onClick={handleCancel} sx={{ color: "white" }}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            sx={{
              bgcolor: "#1b8bd1",
              "&:hover": {
                bgcolor: "#166da2",
              },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FilterSelector;
